import { useState, useEffect, useMemo } from 'react';
import { AnalyticsSurfaceCard } from '@repo/ui/components/cards/AnalyticsSurfaceCard';
import Pagination from '@repo/ui/components/Pagination';
import { Spinner } from '@repo/ui/components/Spinner';
import { Search, Brain, HeartPulse, Download } from 'lucide-react';
import { SessionDto } from '@repo/shared/dtos/devices/SessionDto';
import { palette } from '@repo/ui/theme';
import ExportButton from '../../../utils/export/ExportButton';
import { ExportFormat } from '../../../utils/export/types';
import { useInsightsSessions, useAllInsightsSessions } from '../api/useInsightsSessions';
import { SEARCH_DEBOUNCE_MS } from '../config/constants';
import { SessionInsightsModal } from './SessionInsightsModal';

export const SessionHistoryCard = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedSession, setSelectedSession] = useState<SessionDto | null>(null);
  const [defaultView, setDefaultView] = useState<'heart' | 'brain'>('heart');
  const [exportEnabled, setExportEnabled] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const { data, isLoading, isError } = useInsightsSessions({
    searchQuery: debouncedQuery,
    page,
    limit: 5,
  });

  const { data: allSessions, isFetching: isExportFetching } = useAllInsightsSessions({
    enabled: exportEnabled,
  });

  const sessions = data?.sessions || [];
  const totalPages = data?.totalPages || 1;

  const exportData = useMemo(
    () =>
      allSessions?.map(s => {
        const d = new Date(s.createdts);
        return {
          sessionId: s.expsessionid,
          experienceName: s.experienceName,
          experienceDescription: s.experienceDescription,
          unitName: s.unitName ?? (s.unitid ? `Unit ${s.unitid}` : ''),
          date: d.toISOString().split('T')[0],
          time: d.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
          }),
        };
      }) ?? [],
    [allSessions],
  );

  const openModal = (session: SessionDto, view: 'heart' | 'brain') => {
    setSelectedSession(session);
    setDefaultView(view);
  };

  return (
    <>
      <AnalyticsSurfaceCard
        className="mx-auto w-full max-w-3xl"
        accentColor={palette.indigo}
        header={
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-[var(--anl-text)]">Session History</div>
            {!exportEnabled && (
              <button
                onClick={() => setExportEnabled(true)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-[var(--anl-text-muted)] transition-colors hover:bg-[var(--anl-surface-elev)] hover:text-[var(--anl-text)]"
                title="Export session history"
              >
                <Download size={14} />
                Export
              </button>
            )}
            {exportEnabled && isExportFetching && <Spinner size="sm" />}
            {exportEnabled && !isExportFetching && exportData.length > 0 && (
              <ExportButton
                data={exportData}
                config={{ fileName: 'session_history', date: true }}
                format={ExportFormat.BOTH}
              />
            )}
          </div>
        }
      >
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search size={18} className="text-[var(--anl-text-muted)]" />
          </div>
          <input
            type="text"
            placeholder="Search sessions..."
            className="w-full rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] py-2 pl-10 pr-4 text-[var(--anl-text)] placeholder:text-[var(--anl-text-muted)] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--anl-accent)]"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="relative mt-4 min-h-80">
          {isLoading ? (
            <EmptyState message="Loading sessions..." />
          ) : isError ? (
            <EmptyState message="Error loading sessions" isError />
          ) : sessions.length === 0 ? (
            <EmptyState message="No sessions found" />
          ) : (
            sessions.map(session => (
              <SessionItem key={session.expsessionid} session={session} onOpen={openModal} />
            ))
          )}
        </div>

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          variant="dark"
        />
      </AnalyticsSurfaceCard>

      {selectedSession && (
        <SessionInsightsModal
          session={selectedSession}
          defaultView={defaultView}
          onClose={() => setSelectedSession(null)}
        />
      )}
    </>
  );
};

const SessionItem = ({
  session,
  onOpen,
}: {
  session: SessionDto;
  onOpen: (session: SessionDto, view: 'heart' | 'brain') => void;
}) => {
  const { monthDay, time } = session.formattedDate || { monthDay: '', time: '' };
  const hasBiosensor = session.hasPpgData || session.hasEegData;
  const defaultView = session.hasPpgData ? 'heart' : 'brain';

  return (
    <div
      role={hasBiosensor ? 'button' : undefined}
      tabIndex={hasBiosensor ? 0 : undefined}
      onClick={() => hasBiosensor && onOpen(session, defaultView)}
      onKeyDown={e => {
        if ((e.key === 'Enter' || e.key === ' ') && hasBiosensor) {
          if (e.key === ' ') e.preventDefault();
          onOpen(session, defaultView);
        }
      }}
      className={`flex items-center justify-between rounded-lg border-b border-[var(--anl-border)] p-3 transition-all ${hasBiosensor ? 'cursor-pointer hover:bg-[var(--anl-surface-elev)]' : ''}`}
    >
      <div className="flex max-w-[55%] flex-col">
        <span className="truncate font-medium text-[var(--anl-text)]">
          {session.experienceName}
        </span>
        <span className="line-clamp-1 text-sm text-[var(--anl-text-muted)]">
          {session.experienceDescription}
        </span>
        <span className="truncate text-xs" style={{ color: palette.yellow }}>
          {session.unitName ?? (session.unitid ? `Unit ${session.unitid}` : '')}
        </span>
      </div>

      <div className="ml-3 flex shrink-0 items-center gap-2">
        {session.hasEegData && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onOpen(session, 'brain');
            }}
            title="View brain insights"
            className="flex h-7 w-7 items-center justify-center rounded-full transition-opacity hover:opacity-80"
            style={{ backgroundColor: palette.indigo }}
          >
            <Brain size={14} className="text-white" strokeWidth={2} />
          </button>
        )}
        {session.hasPpgData && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onOpen(session, 'heart');
            }}
            title="View heart insights"
            className="flex h-7 w-7 items-center justify-center rounded-full transition-opacity hover:opacity-80"
            style={{ backgroundColor: palette.red }}
          >
            <HeartPulse size={14} className="text-white" strokeWidth={2} />
          </button>
        )}
        <div className="flex flex-col items-end">
          <span className="font-medium text-[var(--anl-text)]">{monthDay}</span>
          <span className="text-sm text-[var(--anl-text-muted)]">{time}</span>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ message, isError = false }: { message: string; isError?: boolean }) => (
  <div
    className={`py-4 text-center text-sm ${isError ? 'text-[var(--anl-error)]' : 'text-[var(--anl-text-muted)]'}`}
  >
    {message}
  </div>
);
